/** D14 纪元跃迁:ceremony 组·全项目最重仪式(跪拜→闪白→火种脉动→字幕→光尘→烟花余韵)。
 *
 *  黎明纪(epochKey='dawn')走专属加强版——"第一次日出:全屏金光,浮岛从云海中显形"
 *  (EPOCHS[1] 原文,此前从未被实现):
 *   0-3s  跪拜 + 火种膨胀(同基础版)
 *   3s    闪金 + 钟声 + 天光 override 拉向黎明金 + 太阳自地平线升起
 *   3-10s 太阳缓升,金色光尘飘落,世界从混沌灰上色(World 侧 chaos→0 同步进行)
 *   10s+  override 缓释,交还给真实时刻的天空
 */
import { Sprite } from 'pixi.js';
import { BaseEffect, EffectGroup } from './base';
import { scene } from '../engine/Scene';
import { sky } from '../engine/SkyRenderer';
import { eventPool } from '../engine/ParticlePool';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { LW, LH } from '../engine/Stage';
import { rand, pick } from '../engine/util';
import { sfx } from '../engine/Sfx';

export class EpochAscendEffect extends BaseEffect {
  id = 'epoch_ascend';
  exclusiveGroup: EffectGroup = 'ceremony';
  private phase = 0;
  private caption?: HTMLElement;
  private dustT = 0;
  private epochName = '新纪元';
  private burstTimer = 0;
  private bursts = 0;
  private dawn = false;
  private sunHalo?: Sprite;
  private sunCore?: Sprite;
  private overrideCleared = false;

  onStart(meta?: any) {
    this.dur = 8; this.tail = 2;
    this.epochName = meta?.epochName ?? '新纪元';
    this.dawn = meta?.epochKey === 'dawn';
    scene.crowd?.pray(4);
    scene.fireSeed?.setSize(2.4);   // 前奏:火种膨胀
    if (this.dawn) {
      this.dur = 11; this.tail = 3;
      // 天光拉向黎明金(4s 渐入);ambient 提到 0.62 让剪影对比随之柔化。
      // celestial:'none' —— 仪式期间令 Celestial 常驻太阳淡出,由仪式日轮独占
      // 舞台(否则真实时刻是白昼时会出现"双日同框"穿帮);override 释放后常驻
      // 太阳自然淡回,完成交接。
      sky.overrideTo({ top: 0x3a2b57, mid: 0xc9805a, bottom: 0xffd9a0, ambient: 0.62, celestial: 'none' }, 4);
    }
  }

  onUpdate(dt: number) {
    // ② 爆发(3s 后)
    if (this.phase === 0 && this.t >= 3) {
      this.phase = 1;
      this.stage.flash.flash(1, 0.5);
      this.stage.shake.shake(4, 0.6);
      scene.fireSeed?.setSize(1);
      this.showCaption();
      sfx.play('bell');
      sfx.play('bell', { delay: 1.2 });
      if (this.dawn) this.buildSun();
    }
    // 黎明:太阳自地平线缓升(3s→9s),有核日轮(§C1 修正案:天体必须有亮度收拢点)
    if (this.dawn && this.sunHalo && this.sunCore) {
      const p = easing.quadOut(Math.max(0, Math.min(1, (this.t - 3) / 6)));
      const x = LW * 0.30, y = LH * 0.72 - p * LH * 0.40;
      this.sunHalo.position.set(x, y);
      this.sunCore.position.set(x, y);
      const a = Math.min(1, p * 2.2);
      this.sunHalo.alpha = 0.4 * a;
      this.sunCore.alpha = 0.9 * a;
    }
    // 黎明:仪式收尾,天光交还真实时刻(只执行一次)
    if (this.dawn && !this.overrideCleared && this.t > this.dur) {
      this.overrideCleared = true;
      sky.clearOverride(8);
    }
    // ③ 新生:divine 光尘飘落
    if (this.phase >= 1) {
      this.dustT -= dt;
      if (this.dustT <= 0) {
        this.dustT = 0.05;
        eventPool?.spawn({ x: rand(0, LW), y: -20, vx: rand(-10, 10), vy: rand(60, 120), life: rand(3, 5), scale: 0.03, tint: P.divine, alpha: 0.8, blend: 'add', texture: TEX.glow(), fade: 'quad' });
      }
    }
    // 庆典烟花余韵(dt 驱动,5 连发)
    if (this.phase === 1 && this.t >= 5) this.phase = 2;
    if (this.phase === 2 && this.bursts < 5) {
      this.burstTimer -= dt;
      if (this.burstTimer <= 0) { this.burstTimer = 0.5; this.bursts++; this.burst(); }
    }
    // 字幕淡入淡出
    if (this.caption) {
      const age = this.t - 3;
      const a = this.t > this.dur ? Math.max(0, 1 - (this.t - this.dur) / 1.5) : Math.min(1, age / 0.6);
      this.caption.style.opacity = String(Math.max(0, a));
    }
  }

  private showCaption() {
    // 纪元名:左上竖排(Captain 定稿)——古碑题字式,不再横占画面中央
    const el = document.createElement('div');
    el.textContent = this.epochName;
    el.style.cssText =
      'position:fixed;left:7%;top:16%;z-index:50;font-size:52px;font-weight:800;' +
      'writing-mode:vertical-rl;letter-spacing:14px;' +
      'color:#ffe9c4;-webkit-text-stroke:1.5px rgba(0,0,0,.4);opacity:0;pointer-events:none;' +
      'font-family:"Songti SC","Noto Serif SC","SimSun",serif;text-shadow:0 0 30px rgba(255,233,196,.6)';
    document.body.appendChild(el);
    this.caption = el;
  }

  private burst() {
    const x = rand(LW * 0.3, LW * 0.7), y = rand(LH * 0.15, LH * 0.4);
    for (let j = 0; j < 70; j++) {
      const a = (j / 70) * Math.PI * 2;
      const sp = rand(200, 300);
      eventPool?.spawn({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, ay: 90, life: 1.2, scale: 0.03, tint: pick([P.ember, P.emberHi, P.divine]), alpha: 0.9, blend: 'add', texture: TEX.core(), fade: 'quad' });
    }
  }

  /** 黎明日轮:halo + 软核,挂在效果容器,仪式结束随容器销毁(之后由 Celestial 常驻接管) */
  private buildSun() {
    this.sunHalo = new Sprite(TEX.glow());
    this.sunHalo.anchor.set(0.5); this.sunHalo.blendMode = 'add'; this.sunHalo.tint = 0xffb974;
    this.sunHalo.scale.set(1.3); this.sunHalo.alpha = 0;
    this.sunCore = new Sprite(TEX.core());
    this.sunCore.anchor.set(0.5); this.sunCore.blendMode = 'add'; this.sunCore.tint = 0xffe2b0;
    this.sunCore.scale.set(0.5); this.sunCore.alpha = 0;
    this.g.addChild(this.sunHalo, this.sunCore);
  }

  onDestroy() {
    this.caption?.remove();
    scene.fireSeed?.setSize(1);
    if (this.dawn && !this.overrideCleared) sky.clearOverride(5);
  }
}
